import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import useRDService from "../../../CustomHooks/useRDService";
import { ArrowLeft, CheckCircle } from "lucide-react";
import $ from "jquery";

const STEPS = { FORM: "FORM", OTP: "OTP", BIOMETRIC: "BIOMETRIC", DONE: "DONE" };

const STEP_LABELS = {
  [STEPS.FORM]:      "1. Start eKYC",
  [STEPS.OTP]:       "2. OTP Verification",
  [STEPS.BIOMETRIC]: "3. Biometric Capture",
  [STEPS.DONE]:      "✓ Complete",
};

const stepOrder = [STEPS.FORM, STEPS.OTP, STEPS.BIOMETRIC, STEPS.DONE];

export default function SubhearnEkyc({ onBack, onSuccess }) {
  const username  = useSelector((s) => s.auth?.employeeDetails?.username);
  const { lat, lng } = useSelector((s) => s.location);

  const mid       = username || "";
  const latitude  = lat?.toString() || "";
  const longitude = lng?.toString() || "";

  const { captureFingerprint, loading: rdLoading } = useRDService();

  const { callApi: getKycStatusApi }   = useApi("/aeps/subhearn/bank1/bank-kyc-status",   "GET");
  const { callApi: onboardApi }        = useApi("/aeps/subhearn/bank1/onboard-from-user", "POST");
  const { callApi: sendOtpApi }        = useApi("/aeps/subhearn/bank1/send-otp",          "POST");
  const { callApi: verifyOtpApi }      = useApi("/aeps/subhearn/bank1/verify-otp",        "POST");
  const { callApi: verifyBiometricApi }= useApi("/aeps/subhearn/bank1/verify-biometric",  "POST");

  const [kycCompleted,  setKycCompleted]  = useState(false);
  const [kycStatusData, setKycStatusData] = useState({ message: "", ekycDone: false, bankKycDone: false });

  const [step,    setStep]    = useState(STEPS.FORM);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [otp,     setOtp]     = useState("");
  const [phase,   setPhase]   = useState(1);

  const showMsg = (msg, err = false) => { setMessage(msg); setIsError(err); };

  useEffect(() => {
    if (!mid) return;
    const checkKyc = async () => {
      try {
        const res = await getKycStatusApi(null, `mid=${mid}`);
        if (res?.bankKycDone === true) {
          setKycStatusData({ message: res?.message || "Bank KYC is done", ekycDone: res?.ekycDone, bankKycDone: res?.bankKycDone });
          setKycCompleted(true);
        }
      } catch { /* silent */ }
    };
    checkKyc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mid]);

  const parsePidXml = (xml) => {
    const $xml = $(xml);
    const get = (sel, attr) => $xml.find(sel).attr(attr) || "0";
    const infoValue = (name) =>
      $xml.find(`Info[name='${name}']`).attr("value") ||
      $xml.find(`Param[name='${name}']`).attr("value") || "0";
    return {
      srno:        infoValue("srno"),
      errCode:     get("Resp", "errCode"),
      errInfo:     get("Resp", "errInfo"),
      fCount:      get("Resp", "fCount"),
      fType:       get("Resp", "fType"),
      nmPoints:    get("Resp", "nmPoints"),
      qScore:      get("Resp", "qScore"),
      dpId:        get("DeviceInfo", "dpId"),
      rdsId:       get("DeviceInfo", "rdsId"),
      rdsVer:      get("DeviceInfo", "rdsVer"),
      dc:          get("DeviceInfo", "dc"),
      mi:          get("DeviceInfo", "mi"),
      mc:          get("DeviceInfo", "mc"),
      ci:          get("Skey", "ci"),
      sessionKey:  $xml.find("Skey").text() || "0",
      hmac:        $xml.find("Hmac").text()  || "0",
      pidDataType: get("Data", "type"),
      pidData:     $xml.find("Data").text()  || "0",
    };
  };

  const isSuccess = (res) =>
    res?.success === true ||
    res?.status === true ||
    res?.status === "SUCCESS" ||
    res?.statuscode === "TXN" ||
    (typeof res?.message === "string" && res.message.toLowerCase().includes("success"));

  /* ── STEP 1: Onboard ── */
  const handleOnboard = async () => {
    setLoading(true);
    showMsg("");
    try {
      const res = await onboardApi({ mid, latitude, longitude });
      if (isSuccess(res)) {
        showMsg(res?.message || "Onboarding submitted. Sending OTP…");
        await doSendOtp();
        setStep(STEPS.OTP);
      } else {
        showMsg(res?.message || "Onboarding failed. Please try again.", true);
      }
    } catch (err) {
      showMsg(err?.response?.data?.message || err?.message || "Onboarding failed.", true);
    } finally {
      setLoading(false);
    }
  };

  /* ── STEP 2a: Send OTP ── */
  const doSendOtp = async () => {
    try {
      const res = await sendOtpApi({
        mid,
        deviceIMEI: "WEB",
        latitude,
        longitude,
      });
      showMsg(res?.message || "OTP sent to registered mobile.");
    } catch (err) {
      showMsg(err?.response?.data?.message || err?.message || "OTP send failed.", true);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    await doSendOtp();
    setLoading(false);
  };

  /* ── STEP 2b: Verify OTP ── */
  const handleVerifyOtp = async () => {
    if (!otp.trim()) return showMsg("Please enter OTP.", true);
    setLoading(true);
    showMsg("");
    try {
      const res = await verifyOtpApi({
        mid,
        deviceIMEI: "WEB",
        latitude,
        longitude,
        otp,
      });
      if (isSuccess(res)) {
        showMsg("OTP verified. Please capture biometric.");
        setOtp("");
        setStep(STEPS.BIOMETRIC);
      } else {
        showMsg(res?.message || "OTP verification failed.", true);
      }
    } catch (err) {
      showMsg(err?.response?.data?.message || err?.message || "OTP verification failed.", true);
    } finally {
      setLoading(false);
    }
  };

  /* ── STEP 3: Biometric ── */
  const handleBiometric = async () => {
    setLoading(true);
    showMsg("Place finger on scanner…");
    try {
      const wadhValue = "E0jzJ/P8UopUHAieZn8CKqS4WPMi5ZSYXgfnlfkWjrc=";
      const pidXml = await captureFingerprint(wadhValue);
      if (!pidXml || !pidXml.includes('errCode="0"')) {
        return showMsg("Fingerprint capture failed. Please try again.", true);
      }
      const pidData = parsePidXml(pidXml);
      const res = await verifyBiometricApi({
        mid,
        latitude,
        longitude,
        biometricData: pidData,
      });
      const resMsg = res?.msg || res?.message || "";
      if (isSuccess(res) && resMsg.toLowerCase().includes("bank ekyc")) {
        // Phase 1 complete — automatically start Bank eKYC (Phase 2)
        setPhase(2);
        showMsg(resMsg);
        await doSendOtp();
        setStep(STEPS.OTP);
      } else if (isSuccess(res)) {
        // Phase 2 complete — all done
        showMsg("eKYC completed successfully!");
        setStep(STEPS.DONE);
        if (onSuccess) setTimeout(onSuccess, 2000);
      } else {
        showMsg(resMsg || "Biometric verification failed.", true);
      }
    } catch (err) {
      showMsg(err?.response?.data?.message || err?.message || "Biometric failed.", true);
    } finally {
      setLoading(false);
    }
  };

  const stepIdx = stepOrder.indexOf(step);

  /* ── KYC already completed (same UI as EkycComponent biometricCompleted block) ── */
  if (kycCompleted) {
    return (
      <div className="overflow-hidden bg-gray-100 p-4 flex items-center">
        <div className="w-full bg-white rounded-2xl shadow-xl p-6">
          <div className="flex justify-between items-center mb-6 border-b pb-4">
            <button onClick={onBack} className="px-4 py-2 bg-gray-100 rounded-lg">
              ← Back to AEPS
            </button>
          </div>
          <h2 className="text-2xl font-semibold mb-6">E-KYC Verification</h2>
          <div className="text-center py-10">
            <h3 className="text-2xl text-green-600 mb-6">
              ✅ {kycStatusData.message}
            </h3>
            <div className="bg-green-50 rounded-xl p-6">
              <p><b>eKYC:</b> Completed</p>
              <p><b>Bank KYC:</b> Completed</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── main render ── */
  return (
    <div className="bg-gray-100 p-4 min-h-screen">
      <div className="w-full bg-white rounded-2xl shadow-xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-blue-600">
            <ArrowLeft size={16} /> Back to AEPS
          </button>
          <h2 className="text-xl font-bold text-gray-800">eKYC Onboarding</h2>
          <span className="text-sm text-gray-400">{STEP_LABELS[step]}</span>
        </div>

        {/* Step progress */}
        <div className="flex items-center px-6 pt-4 pb-2 gap-2">
          {stepOrder.slice(0, -1).map((s, i) => (
            <React.Fragment key={s}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                ${i < stepIdx ? "bg-green-500 text-white" : i === stepIdx ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-400"}`}>
                {i < stepIdx ? "✓" : i + 1}
              </div>
              {i < 2 && <div className={`flex-1 h-1 rounded ${i < stepIdx ? "bg-green-400" : "bg-gray-200"}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="p-6">

          {/* ──────── STEP: FORM ──────── */}
          {step === STEPS.FORM && (
            <div className="space-y-6 py-4">
              <div className="flex flex-col items-center text-center gap-4 py-6">
                <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center">
                  <svg className="w-10 h-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-1">eKYC Onboarding</h3>
                  <p className="text-gray-500 text-sm max-w-sm">
                    Click below to start your eKYC onboarding. Your merchant details will be fetched automatically.
                  </p>
                </div>
                <div className="w-full bg-blue-50 rounded-xl p-4 text-sm text-left text-blue-800 border border-blue-100">
                  <p className="font-semibold mb-1">Merchant ID</p>
                  <p className="font-mono text-base">{mid || "—"}</p>
                </div>
              </div>

              {message && <Msg text={message} error={isError} />}

              <button
                onClick={handleOnboard} disabled={loading}
                className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl disabled:opacity-50 transition"
              >
                {loading ? "Starting…" : "Start eKYC"}
              </button>
            </div>
          )}

          {/* ──────── STEP: OTP ──────── */}
          {step === STEPS.OTP && (
            <div className="space-y-5 py-4">
              <h3 className="text-xl font-bold text-gray-800">Enter OTP</h3>
              <p className="text-sm text-gray-500">OTP sent to your registered mobile number.</p>
              <input
                type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit OTP" inputMode="numeric" maxLength={6}
                className="w-full h-14 border-2 rounded-xl px-6 text-center text-2xl font-bold tracking-widest focus:border-blue-500 focus:outline-none"
              />
              {message && <Msg text={message} error={isError} />}
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={handleVerifyOtp} disabled={loading || !otp}
                  className="py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl disabled:opacity-50 text-base"
                >
                  {loading ? "Verifying…" : "Verify OTP"}
                </button>
                <button
                  onClick={handleResendOtp} disabled={loading}
                  className="py-3.5 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 disabled:opacity-50 text-base"
                >
                  Resend OTP
                </button>
              </div>
            </div>
          )}

          {/* ──────── STEP: BIOMETRIC ──────── */}
          {step === STEPS.BIOMETRIC && (
            <CaptureStep
              title="Aadhaar Biometric Capture"
              subtitle="Place your finger on the scanner to verify your Aadhaar identity."
              loading={loading || rdLoading}
              message={message}
              isError={isError}
              onCapture={handleBiometric}
              btnLabel="Capture Biometric"
            />
          )}

          {/* ──────── STEP: DONE ──────── */}
          {step === STEPS.DONE && (
            <div className="text-center py-10">
              <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-green-700 mb-2">eKYC Completed!</h3>
              {message && <Msg text={message} />}
              <button onClick={onBack} className="mt-6 px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition">
                Back to AEPS
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Msg({ text, error }) {
  return (
    <div className={`p-3 rounded-xl text-sm text-center font-medium ${error ? "bg-red-50 text-red-700 border border-red-200" : "bg-blue-50 text-blue-700 border border-blue-200"}`}>
      {text}
    </div>
  );
}

function CaptureStep({ title, subtitle, loading, message, isError, onCapture, btnLabel }) {
  return (
    <div className="py-6 space-y-6">
      <div className="flex items-center gap-6">
        <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
          <svg className={`w-10 h-10 text-blue-600 ${loading ? "animate-pulse" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
          </svg>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-1">{title}</h3>
          <p className="text-gray-500 text-sm">{loading ? "Waiting for device scan…" : subtitle}</p>
        </div>
      </div>
      {message && <Msg text={message} error={isError} />}
      <button
        onClick={onCapture} disabled={loading}
        className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white text-base font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition"
      >
        {loading ? "Processing…" : btnLabel}
      </button>
    </div>
  );
}
